import React, { Component, Dispatch, SetStateAction } from 'react';

import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import ModalDialog, {
  ModalBody,
  ModalHeader,
  ModalTitle,
} from '@atlaskit/modal-dialog';
import { Box, Inline, Text } from '@atlaskit/primitives';
import AKSpinner from '@atlaskit/spinner';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { RelativeDate } from '@atlassian/bitkit-date';
import {
  getPermalink,
  updateHashInURL,
} from '@atlassian/bitkit-diff/exports/diff-permalink';

import { CodeReviewConversation } from 'src/components/conversation-provider/types';
import { conversationIdToTopCommentId } from 'src/components/conversation-provider/utils/to-conversation';
import { CommentTree } from 'src/components/conversation/src/components/comment-tree';
import { FabricUserAvatar } from 'src/containers/fabric-user-avatar';
import { FabricUserName } from 'src/containers/fabric-user-name';
import { commonMessages } from 'src/i18n';
import { getGlobalIsColorBlindModeEnabled } from 'src/redux/diff-settings';
import scrollTo from 'src/redux/global/actions/scroll-to';
import {
  CLOSE_OUTDATED_COMMENTS_DIALOG,
  FETCH_BASE_REV_COMMENT_CONTEXT,
  FETCH_OUTDATED_COMMENT_CONTEXT,
  onEditorOpenStateChange,
  onPermalinkHashChange,
} from 'src/redux/pull-request/actions';
import {
  getHighlightCommentThread,
  getHighlighted,
  getIsFileRemovedFromDiff,
  getIsLoadingOutdatedCommentContext,
  getOutdatedCommentsDialogFile,
  getisLoadingBaseRevCommentContext,
  getCommentTree,
  getIsLoadingAllChangesCommentContext,
} from 'src/redux/pull-request/selectors';
import { isFileLevelComment } from 'src/redux/pull-request/utils/comments';
import { Link } from 'src/router/components';
import history from 'src/router/history';
import { RouteComponentProps } from 'src/router/types';
import { withRouter } from 'src/router/utils';
import { CommentThreadWrapper } from 'src/sections/repository/styles/page';
import {
  getAllConversationsForFile,
  getBaseRevConversationsForFile,
  getAllChangesConversationsForFile,
  getOutdatedConversationsForFile,
} from 'src/selectors/conversation-selectors';
import { getSyntaxHighlightingEnabled } from 'src/selectors/feature-selectors';
import { Diff as DiffType } from 'src/types/pull-request';
import { BucketDispatch, BucketState } from 'src/types/state';
import urls from 'src/urls';
import {
  extractFilepath,
  extractPrevFilepath,
} from 'src/utils/extract-file-path';
import { shortHash } from 'src/utils/short-hash';

import DiscardCommentsDialog from '../../components/discard-comments-dialog';
import {
  IterativeReviewSelectedDiffState,
  SelectedDiffType,
} from '../../components/tabs/diff/diff-state-context';

import messages from './comments-dialog.i18n';
import * as styles from './comments-dialog.style';
import {
  ConversationWithContext,
  RenderConversationOptions,
  shouldHighlightCurrentConversation,
} from './conversation-with-context';

type StateProps = {
  file: DiffType;
  filename: string | undefined;
  isFileRemoved: boolean;
  conversations: CodeReviewConversation[];
  isColorBlindModeEnabled: boolean;
  isLoadingOutdatedCommentContext: boolean;
  isLoadingBaseRevCommentContext: boolean;
  isLoadingAllChangesCommentContext: boolean;
  isSyntaxHighlightingEnabled: boolean;
  isCommentThreadHighlighted?: boolean | null;
  highlightedCommentId: number | null;
  commentTree: CommentTree;
};

type DispatchProps = {
  onClose: () => void;
  dispatchContextLines: (file?: DiffType) => void;
  scrollToCommentInDialog: () => void;
  onEditorOpenStateChange: (isOpen: boolean) => void;
  onPermalinkHashChange: (hash: string) => void;
};

type MergeProps = StateProps &
  DispatchProps & {
    fetchContextLines: () => void;
  };

type OwnProps = {
  heading?: string;
  selectedDiffType?: SelectedDiffType;
  setIterativeReviewSelectedDiff?: Dispatch<
    SetStateAction<IterativeReviewSelectedDiffState>
  >;
};

type CommentsDialogProps = MergeProps & OwnProps & RouteComponentProps;

type CommentsDialogState = {
  showDiscardCommentsDialog: boolean;
};

const COMMENT_HASH_PREFIX = '#comment-';

const mapStateToProps = (
  state: BucketState,
  ownProps: OwnProps
): StateProps => {
  const { file, isFileOutdated } = getOutdatedCommentsDialogFile(state);

  const getAllConversations = () => {
    // An "outdated" file can have conversations that aren't outdated
    // so we grab all conversations for that file
    if (isFileOutdated) {
      return getAllConversationsForFile(state, file);
    } else if (
      ownProps.selectedDiffType === SelectedDiffType.Cslr ||
      ownProps.selectedDiffType === SelectedDiffType.Custom ||
      ownProps.selectedDiffType === SelectedDiffType.Single
    ) {
      // Show all changes and outdated comments if user picked range is selected
      return getAllChangesConversationsForFile(state, file).concat(
        getOutdatedConversationsForFile(state, file)
      );
    } else {
      // Show outdated and base rev comments if user is on all changes
      return getOutdatedConversationsForFile(state, file).concat(
        getBaseRevConversationsForFile(state, file)
      );
    }
  };

  const filepath = extractFilepath(file);
  const filename = filepath.split('/').pop();
  const isFileRemoved = getIsFileRemovedFromDiff(state, filepath);

  // Filter out file-level comments, unless file has been removed
  const conversations = getAllConversations().filter(
    conversation => !isFileLevelComment(conversation) || isFileRemoved
  );

  return {
    file,
    filename,
    isFileRemoved,
    conversations,
    isColorBlindModeEnabled: getGlobalIsColorBlindModeEnabled(state),
    isLoadingOutdatedCommentContext: getIsLoadingOutdatedCommentContext(state),
    isLoadingBaseRevCommentContext: getisLoadingBaseRevCommentContext(state),
    isLoadingAllChangesCommentContext:
      getIsLoadingAllChangesCommentContext(state),
    isSyntaxHighlightingEnabled: getSyntaxHighlightingEnabled(state),
    highlightedCommentId: getHighlighted(state),
    isCommentThreadHighlighted: getHighlightCommentThread(state),
    commentTree: getCommentTree(state),
  };
};

const mapDispatchToProps = (dispatch: BucketDispatch): DispatchProps => {
  return {
    onClose: () => dispatch({ type: CLOSE_OUTDATED_COMMENTS_DIALOG }),
    dispatchContextLines: file => {
      dispatch({
        type: FETCH_OUTDATED_COMMENT_CONTEXT.REQUEST,
        payload: { file },
      });
      dispatch({
        type: FETCH_BASE_REV_COMMENT_CONTEXT.REQUEST,
        payload: { file },
      });
    },
    scrollToCommentInDialog: () =>
      dispatch(
        scrollTo({ targetId: getPermalink() || '', customBehavior: 'instant' })
      ),
    onEditorOpenStateChange: isOpen =>
      dispatch(onEditorOpenStateChange(isOpen)),
    onPermalinkHashChange: hash => dispatch(onPermalinkHashChange(hash)),
  };
};

const mergeProps = (
  stateProps: StateProps,
  dispatchProps: DispatchProps,
  ownProps: OwnProps
): MergeProps => {
  const { file } = stateProps;

  return {
    ...stateProps,
    ...dispatchProps,
    fetchContextLines: () => dispatchProps.dispatchContextLines(file),
    ...ownProps,
  };
};

export class OutdatedCommentsDialog extends Component<
  CommentsDialogProps,
  CommentsDialogState
> {
  state = {
    showDiscardCommentsDialog: false,
  };

  componentDidMount() {
    this.fetchContextLinesIfNeeded();
    if (!this.props.isLoadingOutdatedCommentContext)
      this.props.scrollToCommentInDialog();
  }

  shouldComponentUpdate(prevProps: CommentsDialogProps) {
    return (
      !this.props.conversations.every(
        (v, i) => v === prevProps.conversations[i]
      ) || prevProps.location.hash !== this.props.location.hash
    );
  }

  componentDidUpdate(prevProps: CommentsDialogProps) {
    this.fetchContextLinesIfNeeded(); // May asynchronously update isLoadingOutdatedCommentContext to true

    if (
      prevProps.isLoadingOutdatedCommentContext &&
      !this.props.isLoadingOutdatedCommentContext
    )
      this.props.scrollToCommentInDialog();

    // attempt to intelligently close dialog when forward/back buttons are used
    if (
      prevProps.location.hash.startsWith(COMMENT_HASH_PREFIX) &&
      !this.props.location.hash.startsWith(COMMENT_HASH_PREFIX)
    ) {
      this.props.onClose();
    }
  }

  fetchContextLinesIfNeeded = () => {
    // We want to make sure that after we re-fetch conversations e.g.
    // pull request reload flag, to be able to re-run contextLines fetching
    // while we see this dialog
    if (this.shouldFetchContextLines()) {
      this.props.fetchContextLines();
    }
  };

  numEditorsOpen = 0;

  isContextLinesAvailable = () =>
    this.props.conversations.filter(
      conversation =>
        conversation.meta &&
        conversation.meta.context_lines !== undefined &&
        !isFileLevelComment(conversation)
    ).length > 0;

  shouldFetchContextLines = () => {
    const { conversations, isFileRemoved } = this.props;

    if (isFileRemoved) return false;

    return (
      !conversations.every(convo => !!isFileLevelComment(convo)) &&
      !this.isContextLinesAvailable()
    );
  };

  handleEditorOpen = () => {
    this.numEditorsOpen++;
    this.props.onEditorOpenStateChange(true);
  };

  handleEditorClose = () => {
    this.numEditorsOpen--;
    if (!this.numEditorsOpen) {
      this.props.onEditorOpenStateChange(false);
    }
  };

  handleClose = () => {
    if (this.numEditorsOpen > 0) {
      this.setState({ showDiscardCommentsDialog: true });
    } else {
      this.closeDialogAndResetHash();
    }
  };

  handleDiscardComments = () => {
    this.setState({ showDiscardCommentsDialog: false });
    this.closeDialogAndResetHash();
  };

  handleDiscardCommentsCancel = () => {
    this.setState({ showDiscardCommentsDialog: false });
  };

  closeDialogAndResetHash = () => {
    const { location } = this.props;
    if (location.hash.startsWith(COMMENT_HASH_PREFIX)) {
      updateHashInURL('', history);
      this.props.onPermalinkHashChange('');
    }

    this.props.onClose();
  };

  renderConversationWithWrapper = (
    conversationId: string,
    options: RenderConversationOptions,
    prConversation: JSX.Element
  ): JSX.Element => (
    <CommentThreadWrapper
      style={
        shouldHighlightCurrentConversation(
          conversationId,
          options.isCommentThreadHighlighted,
          options.highlightedCommentId
        )
          ? { borderColor: `${token('color.border.focused', colors.B100)}` }
          : {}
      }
    >
      {prConversation}
    </CommentThreadWrapper>
  );

  render() {
    const {
      conversations,
      heading,
      file,
      filename,
      isColorBlindModeEnabled,
      isLoadingOutdatedCommentContext,
      isLoadingBaseRevCommentContext,
      isSyntaxHighlightingEnabled,
      isCommentThreadHighlighted,
      highlightedCommentId,
      setIterativeReviewSelectedDiff,
    } = this.props;
    const { showDiscardCommentsDialog } = this.state;
    const { repositoryOwner, repositorySlug, pullRequestId } =
      this.props.match.params;
    const isValidPullRequest =
      repositoryOwner && repositorySlug && pullRequestId;

    if (!file) {
      return null;
    }

    const modalHeading = heading || (
      <FormattedMessage {...messages.otherCommentsTitle} />
    );

    const Header = () => (
      <styles.Header>
        <ModalHeader>
          {/* very strange @emotion/styled ts issue
          //@ts-ignore */}
          <ModalTitle>{modalHeading}</ModalTitle>
        </ModalHeader>
      </styles.Header>
    );

    const Footer = () => (
      <styles.RightAlignedFooter>
        <CustomThemeButton appearance="primary" onClick={this.handleClose}>
          <FormattedMessage {...commonMessages.close} />
        </CustomThemeButton>
      </styles.RightAlignedFooter>
    );

    const renderConversation = (conversation: CodeReviewConversation) => {
      const conversationOptions: RenderConversationOptions = {
        isCommentThreadHighlighted,
        highlightedCommentId,
        handleEditorOpen: this.handleEditorOpen,
        handleEditorClose: this.handleEditorClose,
        canCollapseThread: true,
        canResolveThread: true,
        isColorBlindModeEnabled,
        isSyntaxHighlightingEnabled,
        previousFilePath: extractPrevFilepath(file),
        renderWithWrapper: this.renderConversationWithWrapper,
        meta: {
          path: extractFilepath(file),
          to: Number(file.to || file.from) || null,
        },
      };

      const topCommentId = conversationIdToTopCommentId(
        conversation.conversationId
      );
      if (topCommentId) {
        const commentThread = this.props.commentTree.getThread(topCommentId);
        if (commentThread) {
          return (
            <ConversationWithContext
              commentThread={commentThread}
              options={conversationOptions}
            />
          );
        }
      }
      return <></>;
    };

    return (
      <>
        <ModalDialog
          onClose={this.handleClose}
          autoFocus={false}
          shouldCloseOnEscapePress
          height="100%"
          width="90vw"
        >
          <Header />
          <ModalBody>
            <styles.Container>
              {(isLoadingOutdatedCommentContext ||
                isLoadingBaseRevCommentContext) && (
                <styles.LoadingContainer>
                  <AKSpinner size="medium" />
                </styles.LoadingContainer>
              )}
              {!(
                isLoadingOutdatedCommentContext ||
                isLoadingBaseRevCommentContext
              ) &&
                conversations.map(conversation => {
                  const { createdAt, createdBy } = conversation;
                  const srcRev = conversation.meta.src_rev;
                  const baseRev = conversation.meta.base_rev;
                  const diffRange =
                    baseRev && srcRev
                      ? `${shortHash(srcRev)}..${shortHash(baseRev)}`
                      : null;
                  const isFileLevel = isFileLevelComment(conversation);
                  return (
                    <styles.ConversationContainer
                      key={conversation.conversationId}
                    >
                      <FabricUserAvatar size="medium" user={createdBy} />
                      <styles.Conversation>
                        <Box paddingBlock="space.100">
                          <Inline space="space.150">
                            {isFileLevel ? (
                              /*
                              Because of conversation filtering in mapStateToProps, a file level comment will only be rendered here if the file has been removed.
                              In this case, we want to include the filename for clarity.
                              */
                              <FormattedMessage
                                {...messages.subtitleFileName}
                                values={{
                                  name: (
                                    <Text weight="bold" as="span">
                                      <FabricUserName user={createdBy} />
                                    </Text>
                                  ),
                                  filename,
                                }}
                              />
                            ) : (
                              <FormattedMessage
                                {...messages.subtitleFile}
                                values={{
                                  name: (
                                    <Text weight="bold" as="span">
                                      <FabricUserName user={createdBy} />
                                    </Text>
                                  ),
                                }}
                              />
                            )}
                            {isValidPullRequest &&
                              diffRange &&
                              setIterativeReviewSelectedDiff && (
                                <Text color="color.text.subtlest" as="span">
                                  <Inline space="space.050">
                                    <RelativeDate date={createdAt} />
                                    <FormattedMessage
                                      {...messages.subtitleFileCommitRange}
                                      values={{
                                        range: (
                                          <styles.CommitText>
                                            <Link
                                              to={`${urls.ui.pullRequestTab(
                                                repositoryOwner,
                                                repositorySlug,
                                                pullRequestId,
                                                'diff'
                                              )}/${diffRange}`}
                                              onClick={() =>
                                                setIterativeReviewSelectedDiff({
                                                  sourceHash: srcRev!,
                                                  destHash: baseRev,
                                                  selectedDiffType:
                                                    SelectedDiffType.Custom,
                                                  isLoading: false,
                                                })
                                              }
                                            >
                                              {diffRange}
                                            </Link>
                                          </styles.CommitText>
                                        ),
                                      }}
                                    />
                                  </Inline>
                                </Text>
                              )}
                          </Inline>
                        </Box>
                        {renderConversation(conversation)}
                      </styles.Conversation>
                    </styles.ConversationContainer>
                  );
                })}
            </styles.Container>
          </ModalBody>
          <Footer />
        </ModalDialog>

        {showDiscardCommentsDialog && (
          <DiscardCommentsDialog
            onCancel={this.handleDiscardCommentsCancel}
            onDiscard={this.handleDiscardComments}
          >
            <FormattedMessage
              {...messages.discardCommentsModalBody}
              tagName="p"
              values={{
                outdatedCommentsDialogTitle: (
                  <FormattedMessage
                    {...messages.otherCommentsTitle}
                    tagName="em"
                  />
                ),
              }}
            />
          </DiscardCommentsDialog>
        )}
      </>
    );
  }
}

export default withRouter(
  connect<StateProps, DispatchProps, OwnProps, MergeProps>(
    mapStateToProps,
    mapDispatchToProps,
    mergeProps
  )(OutdatedCommentsDialog)
);
